import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

type SelectionTrigger = { type: 'selectAll' | 'deselectAll'; id: number } | null;
import { FIELD_TYPES, FormFieldConfig, selectCheckBoxValues } from '../../model/form.model';
import { FormGroup,FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CamelCasePipe } from "../../utils/camel-case.pipe"; 
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { SearchFilterPipe } from '../../utils/search-filter.pipe';
import { HighlightPipe } from '../../utils/highlight.pipe';
@Component({
  selector: 'app-distribution-list-checkbox-child',
  standalone: true,
  imports: [MatCheckboxModule,CommonModule,FormsModule, CamelCasePipe, MatExpansionModule, MatProgressSpinnerModule, SearchFilterPipe, HighlightPipe],
  templateUrl: './distribution-list-checkbox-child.component.html',
  styleUrl: './distribution-list-checkbox-child.component.scss',
})
export class DistributionListCheckboxChildComponent implements OnInit , OnChanges  {
  @Input() changedField!: any;
  @Input() field!: FormFieldConfig | undefined;
  @Input() form!: FormGroup;
  @Input() selectionTrigger: SelectionTrigger = null;
  @Input() searchText = '';
  @Output() selectionChange = new EventEmitter<selectCheckBoxValues>();
  @Output() allSelectedChange = new EventEmitter<boolean>();
  selected: string[] = [];  
  readonly FieldType: typeof FIELD_TYPES = FIELD_TYPES;
  groupedItems: { [key: string]: { fieldLabel: string; selected: boolean }[] } = {};
  groupAllSelected: { [key: string]: boolean } = {};
  groupExpanded: { [key: string]: boolean } = {};
  jobTitles: string[] = [];
  allSelected = false;
  isCollapseAll = false;

  preSelectedByGroup: { [key: string]: string[] | 'ALL' } = {};

  constructor(private readonly cd: ChangeDetectorRef) {}

  ngOnInit() {
    this.groupItems();
  }

  ngOnChanges(changes: SimpleChanges) { 
    if (changes['selectionTrigger'] && this.selectionTrigger) {
      const isSelectAll = this.selectionTrigger.type === 'selectAll';
      queueMicrotask(() => {
        this.setAllSelection(isSelectAll);
        this.cd.detectChanges();
      });
      return;
    }
    if ((changes['field'] || changes["changedField"]) && this.field) {
      const incomingValue = this.field.selectedValue;

      this.preSelectedByGroup = {};

      if (incomingValue && typeof incomingValue === 'object') {
        for (const groupTitle of Object.keys(incomingValue)) {
          const val = incomingValue[groupTitle];
          if (typeof val === 'string' && val === `All ${groupTitle}`) {
            this.preSelectedByGroup[groupTitle] = 'ALL';
          } else if (Array.isArray(val)) {
            this.preSelectedByGroup[groupTitle] = val;
          }
        }
      }

      this.groupItems();
      this.emitSelected();
    }

  }

  groupItems() {
    this.groupedItems = {};
    this.groupAllSelected = {};
    this.groupExpanded = {};
    this.jobTitles = [];

    if (this.field?.options) {
      const arr = this.field.options;

      for (const item of arr) {
        if (!this.groupedItems[item.fieldValue]) {
          this.groupedItems[item.fieldValue] = [];
          this.jobTitles.push(item.fieldValue);
          this.groupExpanded[item.fieldValue] = this.isCollapseAll;
        }

        this.groupedItems[item.fieldValue].push({ fieldLabel: item.fieldLabel, selected: false });
      }

      this.applySelection();
    }
  }

  applySelection() {
    for (const title of this.jobTitles) {
      const selections = this.preSelectedByGroup?.[title];

      for (const item of this.groupedItems[title]) {
        if (selections === 'ALL') {
          item.selected = true;
        } else if (Array.isArray(selections)) {
          item.selected = selections.includes(item.fieldLabel);
        } else {
          item.selected = false;
        }
        if (item.selected){
          this.groupExpanded[title] = true
        }
      }

      this.groupAllSelected[title] = this.groupedItems[title].every(i => i.selected);

    }

    this.allSelected = this.jobTitles.every(title =>
      this.groupedItems[title].every(i => i.selected)
    );
    this.allSelectedChange.emit(this.allSelected);
  }

  toggleAllSelection() {
    for (const title of this.jobTitles) {
      this.groupAllSelected[title] = this.allSelected;
      this.groupedItems[title].forEach(item => item.selected = this.allSelected);
    }
    this.emitSelected();
    this.allSelectedChange.emit(this.allSelected);
  }

  toggleGroupSelection(title: string) {
    const isSelected = this.groupAllSelected[title];
    this.groupedItems[title].forEach(item => item.selected = isSelected);
    this.syncAllSelectedState();
    this.emitSelected();
  }

  updateGroupSelectAll(title: string) {
    this.groupAllSelected[title] = this.groupedItems[title].every(i => i.selected);
    this.syncAllSelectedState();
    this.emitSelected();
  }

  syncAllSelectedState() {
    this.allSelected = this.jobTitles.every(title =>
      this.groupedItems[title].every(i => i.selected)
    );
  }

  emitSelected() {
    const selectedByGroup: { [key: string]: string[] | string } = {};

    for (const groupTitle of this.jobTitles) {
      const items = this.groupedItems[groupTitle];
      const selectedItems = items.filter(item => item.selected).map(item => item.fieldLabel);

      if (selectedItems.length === items.length && selectedItems.length > 0) {
        selectedByGroup[groupTitle] = `All ${groupTitle}`;
      } else if (selectedItems.length > 0) {
        selectedByGroup[groupTitle] = selectedItems;
      }
    }
    this.selectionChange.emit({
      FieldName: this.field?.fieldName ?? '',
      Value: selectedByGroup
    });
  }
  extractKeysFromObject = (values: any) => {
    return (Object.values(values).flat() as string[]);
  }

  getSelectedCount(title: string): string {
    const group = this.groupedItems[title];
    const count = group.filter(i => i.selected).length;
    return `[${count}/${group.length}]`;
  }

  isGroupMatch(groupTitle: string): boolean {
    if (!this.searchText?.trim()){ return false };
    const lower = this.searchText.toLowerCase();
    return (
      groupTitle.toLowerCase().includes(lower) ||
      (this.groupedItems[groupTitle]?.some(item => item.fieldLabel.toLowerCase().includes(lower)) ?? false)
    );
  }

  toggleExpand(title: string) {
    this.groupExpanded[title] = !this.groupExpanded[title];
  }

  onGroupSelectClick(groupTitle: string): void {
    this.groupAllSelected[groupTitle] = !this.groupAllSelected[groupTitle];
    this.toggleGroupSelection(groupTitle);
  }

  onItemClick(item: { fieldLabel: string; selected: boolean }, groupTitle: string): void {
    item.selected = !item.selected;
    this.updateGroupSelectAll(groupTitle);
  }

  setAllSelection(select: boolean): void {
    this.allSelected = select;
    this.toggleAllSelection();
  }
}